import { Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { IconBaseProps } from 'react-icons';
import { FiChevronsUp } from 'react-icons/fi';

import { Box } from '../box';
import { Button } from '../button';

type StylesTypeName = 'root' | 'header' | 'content' | 'footer' | 'collapsed';

interface Props {
  icon?: ReactElement<IconBaseProps>;
  classNames?: Partial<Record<StylesTypeName, string>>;
  header?: ReactNode;
  footer?: ReactNode;
  collapsedDefault?: boolean;
  collapsible?: boolean;
  children?: ReactNode | ReactNode[];
  tooltip?: string;
  hotKey?: Pick<KeyboardEvent, 'metaKey' | 'code' | 'ctrlKey'>;
  onStateChanged?: (collapse: boolean) => void;
}
export const DashboardPanel: FC<PropsWithChildren<Props>> = ({
  header,
  footer,
  classNames,
  children,
  collapsedDefault = false,
  onStateChanged,
  hotKey,
  collapsible = true,
  icon,
  tooltip,
}: PropsWithChildren<Props>) => {
  const [isCollapsed, { toggle }] = useDisclosure(collapsedDefault);
  const handleCollapsedClick = useCallback(() => {
    toggle();
    const newValue = !isCollapsed;
    onStateChanged?.(newValue);
  }, [isCollapsed, onStateChanged, toggle]);

  const handleHotKey = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.code === hotKey?.code &&
        (e.ctrlKey === hotKey.ctrlKey || e.metaKey === hotKey.metaKey)
      ) {
        e.preventDefault();
        handleCollapsedClick();
      }
    },
    [handleCollapsedClick, hotKey]
  );

  useEffect(() => {
    if (!collapsible) {
      return;
    }

    if (hotKey) {
      window.addEventListener('keydown', handleHotKey);
      return () => {
        window.removeEventListener('keydown', handleHotKey);
      };
    }
  }, [collapsible, handleHotKey, hotKey]);

  const PanelPreview = (
    <Button
      tooltip={tooltip}
      theme="secondary"
      className={`border-2 border-deep-gray-700 bg-deep-gray-800 text-2xl ${
        classNames?.collapsed || ''
      }`}
      onClick={handleCollapsedClick}
    >
      {icon}
    </Button>
  );

  const PanelContent = (
    <Box className={`flex  flex-col ${classNames?.root || ''}`}>
      {header && (
        <div
          className={`flex select-none items-center gap-2 whitespace-nowrap text-center text-2xl font-bold ${
            classNames?.header || ''
          }`}
        >
          <div className="flex w-full items-center gap-2 text-center">
            {icon}
            {header}
          </div>
          {collapsible && (
            <div className="flex">
              <Tooltip label={tooltip} disabled={!tooltip}>
                <button type="button" onClick={handleCollapsedClick} aria-label="Collapse panel">
                  <FiChevronsUp className="-rotate-90" />
                </button>
              </Tooltip>
            </div>
          )}
        </div>
      )}
      {/* <div className={`overflow-y-scroll  ${classNames?.content || ''}`}> */}
      <div className={` ${classNames?.content || ''}`}>{children}</div>
      {footer && (
        <div className={`p-2 ${classNames?.footer || ''}`}>{footer}</div>
      )}
    </Box>
  );

  if (collapsible) {
    return isCollapsed ? PanelPreview : PanelContent;
  }

  return PanelContent;
};
